import React, { useState, useEffect } from 'react';

export default function AdminModal({ isOpen, onClose, onSubmit, isAdmin = false, onLogout }) {
    const [key, setKey] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!key.trim()) {
            setError('Wprowadź klucz administracyjny');
            return;
        }

        try {
            setIsLoading(true);
            await onSubmit(key);
            setKey('');
            onClose();
        } catch (err) {
            setError(err.message || 'Nieprawidłowy klucz');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={modalOverlayStyle} onClick={onClose}>
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                <div style={modalHeaderStyle}>
                    <div style={iconContainerStyle}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                    </div>
                    <div>
                        <h2 style={titleStyle}>Panel administracyjny</h2>
                        <p style={subtitleStyle}>
                            {isAdmin ? 'Tryb administratora jest aktywny' : 'Wprowadź klucz, aby kontynuować'}
                        </p>
                    </div>
                    <button onClick={onClose} style={closeButtonStyle} aria-label="Zamknij">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                {isAdmin ? (
                    <div style={adminSessionStyle}>
                        <div style={adminStatusStyle}>
                            <span style={adminStatusDotStyle}></span>
                            Dostęp administracyjny jest aktywny
                        </div>
                        <p style={adminSessionTextStyle}>
                            Wylogowanie usunie zapisany klucz z tego urządzenia i ukryje wszystkie narzędzia administracyjne.
                        </p>
                        <div style={buttonContainerStyle}>
                            <button
                                type="button"
                                onClick={onClose}
                                style={cancelButtonStyle}
                            >
                                Zamknij
                            </button>
                            <button
                                type="button"
                                onClick={onLogout}
                                style={logoutButtonStyle}
                            >
                                Wyloguj się
                            </button>
                        </div>
                    </div>
                ) : (
                <form onSubmit={handleSubmit}>
                    <div style={inputContainerStyle}>
                        <div style={inputWrapperStyle}>
                            <svg style={inputIconStyle} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                            </svg>
                            <input
                                type="password"
                                placeholder="Wprowadź klucz administracyjny..."
                                value={key}
                                onChange={(e) => setKey(e.target.value)}
                                style={inputStyle}
                                autoFocus
                                disabled={isLoading}
                            />
                        </div>
                        {error && (
                            <div style={errorContainerStyle}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <line x1="12" y1="8" x2="12" y2="12"/>
                                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                                </svg>
                                <span>{error}</span>
                            </div>
                        )}
                    </div>

                    <div style={buttonContainerStyle}>
                        <button 
                            type="button" 
                            onClick={onClose} 
                            style={cancelButtonStyle}
                            disabled={isLoading}
                        >
                            Anuluj
                        </button>
                        <button 
                            type="submit" 
                            style={submitButtonStyle}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <span style={loadingContainerStyle}>
                                    <svg style={spinnerStyle} viewBox="0 0 24 24" fill="none">
                                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25"/>
                                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                                    </svg>
                                    Weryfikacja...
                                </span>
                            ) : (
                                'Zaloguj się'
                            )}
                        </button>
                    </div>
                </form>
                )}
            </div>
        </div>
    );
}

const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(41, 4, 1, 0.8)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(8px)',
    animation: 'fadeIn 0.3s ease-out'
};

const modalStyle = {
    background: 'linear-gradient(135deg, #1a0a08 0%, #290401 100%)',
    padding: '32px',
    borderRadius: '16px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
    border: '1px solid rgba(249, 196, 154, 0.2)',
    position: 'relative',
    animation: 'slideUp 0.3s ease-out'
};

const modalHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '24px'
};

const iconContainerStyle = {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #F9C49A 0%, #EAD9CB 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#290401',
    flexShrink: 0
};

const titleStyle = {
    margin: 0,
    fontSize: '22px',
    fontWeight: '600',
    color: '#EAD9CB',
    letterSpacing: '0.5px'
};

const subtitleStyle = {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: 'rgba(234, 217, 203, 0.7)'
};

const closeButtonStyle = {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'transparent',
    border: 'none',
    color: 'rgba(234, 217, 203, 0.6)',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '8px',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const inputContainerStyle = {
    marginBottom: '20px'
};

const adminSessionStyle = {
    display: 'grid',
    gap: '18px'
};

const adminStatusStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '13px 15px',
    color: '#d8f3dc',
    background: 'rgba(74, 180, 106, 0.12)',
    border: '1px solid rgba(117, 214, 145, 0.28)',
    borderRadius: '10px',
    fontSize: '14px'
};

const adminStatusDotStyle = {
    width: '9px',
    height: '9px',
    flexShrink: 0,
    background: '#75d691',
    borderRadius: '50%',
    boxShadow: '0 0 0 4px rgba(117, 214, 145, 0.12)'
};

const adminSessionTextStyle = {
    margin: 0,
    color: 'rgba(234, 217, 203, 0.72)',
    fontSize: '14px',
    lineHeight: '1.6'
};

const inputWrapperStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
};

const inputIconStyle = {
    position: 'absolute',
    left: '16px',
    color: 'rgba(234, 217, 203, 0.5)',
    pointerEvents: 'none'
};

const inputStyle = {
    width: '100%',
    padding: '14px 16px 14px 48px',
    fontSize: '15px',
    backgroundColor: 'rgba(234, 217, 203, 0.08)',
    border: '2px solid rgba(234, 217, 203, 0.15)',
    borderRadius: '10px',
    color: '#EAD9CB',
    transition: 'all 0.3s ease',
    outline: 'none'
};

const errorContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#ff6b6b',
    fontSize: '13px',
    marginTop: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(255, 107, 107, 0.1)',
    borderRadius: '8px'
};

const buttonContainerStyle = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end'
};

const cancelButtonStyle = {
    padding: '12px 20px',
    backgroundColor: 'transparent',
    border: '2px solid rgba(234, 217, 203, 0.2)',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#EAD9CB',
    fontSize: '15px',
    fontWeight: '500',
    transition: 'all 0.3s ease'
};

const submitButtonStyle = {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #F9C49A 0%, #EAD9CB 100%)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#290401',
    fontSize: '15px',
    fontWeight: '600',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const logoutButtonStyle = {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #c65454 0%, #9f3333 100%)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
};

const loadingContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
};

const spinnerStyle = {
    width: '18px',
    height: '18px',
    animation: 'spin 0.8s linear infinite'
};
